import { useEffect, useState } from "react";

export interface CountdownProps {
  /** Zielzeitpunkt als ISO-8601-String. */
  until: string;
  /** Wird einmal aufgerufen, sobald der Zeitpunkt erreicht ist. */
  onExpire?: () => void;
  /** Text vor der Zeitangabe, z. B. "Gültig noch". */
  label?: string;
}

function remainingSeconds(until: string): number {
  return Math.max(0, Math.ceil((new Date(until).getTime() - Date.now()) / 1000));
}

function format(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  // Ab einer Stunde muss die Stundenstelle mit. Sonst erscheint eine Kopplung
  // von 60 Minuten als "59:59" und sieht aus, als liefe sie gleich ab.
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${rest.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

/**
 * Zeigt die verbleibende Zeit bis zu einem Zeitpunkt.
 *
 * Rechnet bei jedem Tick neu aus der Zieluhrzeit, statt einen Zaehler
 * herunterzuzaehlen. Sonst geht die Anzeige falsch, sobald der Tab im
 * Hintergrund war -- Browser drosseln dort die Timer erheblich.
 *
 * Die Ansage erfolgt hoeflich und nur einmal pro Minute, damit ein
 * Screenreader nicht jede Sekunde unterbrochen wird.
 */
export function Countdown({ until, onExpire, label }: CountdownProps): React.ReactElement {
  const [seconds, setSeconds] = useState(() => remainingSeconds(until));

  useEffect(() => {
    setSeconds(remainingSeconds(until));
    const timer = setInterval(() => {
      const left = remainingSeconds(until);
      setSeconds(left);
      if (left === 0) {
        clearInterval(timer);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [until, onExpire]);

  const text = seconds > 0 ? format(seconds) : "abgelaufen";

  return (
    <span
      style={{
        fontSize: "var(--text-small)",
        color: seconds <= 30 ? "var(--color-danger)" : "var(--color-granite)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {label ? `${label} ` : ""}
      <span aria-hidden="true">{text}</span>
      <span className="visually-hidden" aria-live="polite">
        {seconds <= 0
          ? "Abgelaufen"
          : seconds >= 3600
            ? `Noch etwa ${Math.round(seconds / 3600)} Stunden`
            : `Noch ${Math.ceil(seconds / 60)} Minuten`}
      </span>
    </span>
  );
}
